import type { SimCareerView, SimEventView, SimNextStep, SimVote } from '@cruz/shared';
import { reducedMotion } from './Juice.tsx';
import { useEffect, useState } from 'react';
import { CrossMark } from '../components/icons.tsx';
import { NextStepCard } from './NextStep.tsx';
import { Sheet } from './Sheet.tsx';
import { card } from './ui.tsx';

/*
 * What keeps a career moving between promotions: the bar towards the next post, always in
 * sight, the vote the cabildo holds tomorrow, and the count of that vote, ballot by ballot.
 */

/** The post at risk: what fell too low and how long is left to bring it back up. */
export function RiskNote({
  risk,
  first = false,
}: {
  risk: NonNullable<SimCareerView['player']['risk']>;
  /** Just warned: the junta starts talking */
  first?: boolean;
}) {
  const what = risk.stats.map((s) => `${s.label} ${s.have} (mínimo ${s.floor})`).join(', ');
  return (
    <section
      aria-label="Peligra tu cargo"
      className="animate-rise rounded-2xl border-2 border-burgundy bg-burgundy-300/20 px-5 py-4 dark:border-burgundy-300 dark:bg-burgundy/20"
    >
      <p className="text-xs font-semibold tracking-[0.16em] text-burgundy uppercase dark:text-burgundy-300">
        {first ? 'La junta empieza a hablar de ti' : 'Peligra tu cargo'}
      </p>
      <p className="mt-1 text-[1.02rem]">
        Tu cargo de {risk.rank_label.toLowerCase()} peligra: {what}.{' '}
        <strong>
          {risk.events_left === 0
            ? 'Es tu última oportunidad.'
            : `Te ${risk.events_left === 1 ? 'queda 1 evento' : `quedan ${risk.events_left} eventos`} para remontar.`}
        </strong>
      </p>
    </section>
  );
}

function Bar({ value, from, label }: { value: number; from?: number; label: string }) {
  // Starts where it was and fills up to where it is now
  const [shown, setShown] = useState(from ?? value);
  useEffect(() => {
    const t = setTimeout(() => setShown(value), 250);
    return () => clearTimeout(t);
  }, [value]);
  return (
    <div
      role="meter"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={value}
      className="h-2.5 overflow-hidden rounded-full bg-purple/10 dark:bg-cream/10"
    >
      <div
        className="h-full rounded-full bg-gold transition-[width] duration-1000 ease-out"
        style={{ width: `${Math.max(3, Math.min(100, shown))}%` }}
      />
    </div>
  );
}

/**
 * «Hacia Insignia · 62 %». With `from`, the bar moves from there and says how much it
 * moved; a tap opens what the post asks for.
 */
export function RankProgress({
  step,
  from,
  gainLabel = 'esta vez',
}: {
  step: SimNextStep;
  /** The progress before (the same post): the bar moves from it */
  from?: number | null;
  gainLabel?: string;
}) {
  const [open, setOpen] = useState(false);
  // An API not restarted yet after a deploy sends no progress
  if (typeof step.progress !== 'number') return null;
  const gain = from == null ? 0 : step.progress - from;
  return (
    <>
      <button
        type="button"
        aria-label={`Hacia ${step.rank_label}: ${step.progress} %${gain ? ` (${gain > 0 ? '+' : '−'}${Math.abs(gain)} % ${gainLabel})` : ''}. Ver qué pide`}
        onClick={() => setOpen(true)}
        className={`${card} block w-full px-4 py-3 text-left transition duration-150 hover:border-gold active:scale-[0.99]`}
      >
        <span className="mb-1.5 flex items-baseline justify-between gap-3">
          <span className="min-w-0 text-[0.95rem]">
            <span className="text-ink-soft dark:text-cream/70">
              {step.by_election ? 'Hacia las elecciones a ' : 'Hacia '}
            </span>
            <strong className="font-semibold text-purple dark:text-gold-300">
              {step.by_election ? step.rank_label.toLowerCase() : step.rank_label}
            </strong>
          </span>
          <span className="flex-none text-[0.95rem] font-semibold tabular">
            {gain !== 0 && (
              <span
                className={`animate-rise mr-2 ${gain > 0 ? 'text-gold-700 dark:text-gold-300' : 'text-burgundy dark:text-burgundy-300'}`}
              >
                {gain > 0 ? '+' : '−'}
                {Math.abs(gain)} %<span className="sr-only"> {gainLabel}</span>
              </span>
            )}
            {step.progress} %
          </span>
        </span>
        <Bar value={step.progress} from={from ?? undefined} label={`Hacia ${step.rank_label}`} />
        {step.ready && (
          <span className="mt-1.5 block text-sm font-medium text-gold-700 dark:text-gold-300">
            {step.by_election
              ? 'Ya puedes presentarte en las próximas elecciones.'
              : 'Lo cumples todo: el cabildo puede llamarte cualquier día.'}
          </span>
        )}
      </button>
      {open && (
        <Sheet title="Tu próximo paso" onClose={() => setOpen(false)}>
          <NextStepCard step={step} />
        </Sheet>
      )}
    </>
  );
}

const voteTitle = (v: SimVote) =>
  v.kind === 'election' ? 'Mañana, elecciones' : 'Mañana vota el cabildo';

/** The vote the cabildo holds tomorrow: on the post, and how the odds stand. */
export function VoteNote({ vote }: { vote: SimVote }) {
  return (
    <section
      aria-label={voteTitle(vote)}
      className="rounded-2xl border-2 border-gold bg-cream-200 px-5 py-4 dark:bg-gold/10"
    >
      <p className="text-xs font-semibold tracking-[0.16em] text-gold-700 uppercase dark:text-gold-300">
        {voteTitle(vote)}
      </p>
      <p className="mt-1 font-serif text-2xl leading-tight font-semibold">
        {vote.kind === 'election' ? 'Hermano mayor' : vote.target_label}
      </p>
      <p className="mt-2 mb-1.5 flex justify-between text-[0.95rem]">
        <span>{vote.kind === 'election' ? 'Votos que tendrías hoy' : 'Tus opciones'}</span>
        <strong className="tabular">{vote.chance} %</strong>
      </p>
      <Bar value={vote.chance} label={vote.kind === 'election' ? 'Votos' : 'Tus opciones'} />
      <p className="mt-2 text-sm text-ink-soft dark:text-cream/75">
        {vote.kind === 'election'
          ? 'Con la mitad de los votos, ganas. Mañana se cuentan las papeletas.'
          : 'No es seguro. Mañana, al empezar el día, se cuentan los votos.'}
      </p>
    </section>
  );
}

const BALLOTS = 9;

/**
 * The ballots, in an order that keeps it open to the end: the losing side goes first
 * and the winner takes the last ones. How many win follows the odds (a long shot that
 * comes in, comes in by little).
 */
export function ballots(success: boolean, chance: number): boolean[] {
  const yes = success
    ? chance >= 75
      ? 7
      : chance >= 50
        ? 6
        : 5
    : chance < 25
      ? 2
      : chance < 50
        ? 3
        : 4;
  const winners = success ? yes : BALLOTS - yes;
  const losers = BALLOTS - winners;
  const out: boolean[] = [];
  for (let i = 0; i < losers; i++) out.push(!success, success);
  while (out.length < BALLOTS) out.push(success);
  return out.slice(0, BALLOTS);
}

/** Before the event that tells how the vote went: the count, one ballot at a time. */
export function CabildoCount({
  verdict,
  onDone,
}: {
  verdict: NonNullable<SimEventView['verdict']>;
  onDone: () => void;
}) {
  const all = ballots(verdict.success, verdict.chance);
  const [count, setCount] = useState(() => (reducedMotion() ? all.length : 0));
  const finished = count >= all.length;
  useEffect(() => {
    if (finished) return;
    // The last ballot takes longer: that is where it is decided
    const wait = count === 0 ? 900 : count === all.length - 1 ? 1300 : 420;
    const t = setTimeout(() => setCount((c) => c + 1), wait);
    return () => clearTimeout(t);
  }, [count, finished, all.length]);
  useEffect(() => {
    if (finished && verdict.success && !reducedMotion()) navigator.vibrate?.([40, 60, 40]);
  }, [finished, verdict.success]);
  const shown = all.slice(0, count);
  const yes = shown.filter(Boolean).length;
  const no = shown.length - yes;
  const election = verdict.kind === 'election';
  return (
    <section
      aria-labelledby="count-title"
      className="rounded-2xl border-2 border-gold bg-purple px-5 py-6 text-center text-cream dark:bg-night-700"
    >
      <p className="text-xs font-semibold tracking-[0.18em] text-gold-300 uppercase">
        {election ? 'Cabildo general de elecciones' : 'Cabildo de oficiales'}
      </p>
      <h1 id="count-title" className="mt-2 font-serif text-[1.9rem] leading-tight font-semibold">
        {election
          ? '¿Sales elegido hermano mayor?'
          : `¿Te nombran ${verdict.target_label.toLowerCase()}?`}
      </h1>
      <p className="mt-1 text-cream/80">
        {election ? 'Llevabas' : 'Tenías'} un {verdict.chance} %{' '}
        {election ? 'de los votos' : 'de opciones'}.
      </p>
      <ul className="mt-5 flex justify-center gap-1.5" aria-hidden>
        {all.map((b, i) => (
          <li
            key={i}
            className={`flex h-10 w-7 items-center justify-center rounded-md border text-xs font-bold ${
              i >= count
                ? 'border-cream/25 border-dashed'
                : b
                  ? 'animate-crown border-gold bg-gold text-night'
                  : 'animate-crown border-burgundy-300 bg-burgundy text-cream'
            }`}
          >
            {i < count ? (b ? 'Sí' : 'No') : ''}
          </li>
        ))}
      </ul>
      <p className="mt-3 text-lg tabular" aria-hidden>
        Sí <strong>{yes}</strong> · No <strong>{no}</strong>
      </p>
      <div role="status" className="min-h-[5.5rem]">
        {finished ? (
          <div className="animate-crown mt-3">
            {verdict.success && (
              <span className="mx-auto block w-fit text-gold-300">
                <CrossMark size={26} />
              </span>
            )}
            <p className="mt-1 font-serif text-[2.2rem] leading-none font-semibold">
              {verdict.success ? '¡Sí!' : 'Esta vez, no'}
            </p>
            <p className="mt-2 text-cream/80">
              {verdict.success
                ? election
                  ? 'Los hermanos te han elegido.'
                  : 'El cabildo ha dicho que sí.'
                : 'Habrá más cabildos.'}
            </p>
          </div>
        ) : (
          <p className="mt-4 text-cream/70">
            {count === 0 ? 'Se abre la urna…' : 'Se cuentan las papeletas…'}
          </p>
        )}
      </div>
      {finished && (
        <button
          type="button"
          className="mt-4 inline-flex min-h-12 w-full items-center justify-center rounded-xl bg-gold-300 px-5 py-3 text-lg font-semibold text-night transition duration-150 hover:bg-gold active:scale-[0.99]"
          onClick={onDone}
        >
          Ver qué pasa
        </button>
      )}
    </section>
  );
}
